<template>

    <ui-modal class="ui-alert-modal" @close="cancel" :close-button="false">

        <div class="alert-content">
            <span class="title">{{ title }}</span>
            <span class="subtitle" v-if="subtitle">{{ subtitle }}</span>
        </div>

        <slot></slot>

        <ui-modal-buttons v-if="hasButtons">

            <ui-button v-if="cancelButton"
                       color="neutral" type="soft" :icon="cancelButtonIcon"
                       :loading="loadingButtonIndex === 1" :disabled="loadingButtonIndex > 0"
                       @click="close(null, 1)">
                {{ cancelButton !== true ? cancelButton : $t('core.general.cancel') }}
            </ui-button>

            <ui-button
                v-if="negativeButton"
                color="negative" :icon="negativeButtonIcon"
                :loading="loadingButtonIndex === 2" :disabled="loadingButtonIndex > 0"
                @click="close(false, 2)">
                {{ negativeButton !== true ? negativeButton : $t('core.general.no') }}
            </ui-button>

            <ui-button
                v-if="positiveButton"
                color="positive" :icon="positiveButtonIcon"
                :loading="loadingButtonIndex === 3" :disabled="loadingButtonIndex > 0"
                @click="close(true, 3)">
                {{ positiveButton !== true ? positiveButton : $t('core.general.yes') }}
            </ui-button>

        </ui-modal-buttons>
    </ui-modal>

</template>

<script lang="ts">

import { Component, Prop, Vue } from 'vue-facing-decorator';
import { isArray } from 'lodash';

import type { ComponentProps } from '@wittignl/js-core';
import { createComponentMixin } from '@wittignl/js-core';

import Modal from './Modal.vue';
import ModalButtons from './ModalButtons.vue';
import Button from '../button/Button.vue';

export interface AlertOptions {
    title: string
    subtitle?: string
    cancelButton?: string | boolean
    cancelButtonIcon?: string
    negativeButton?: string | boolean
    negativeButtonIcon?: string
    positiveButton?: string | boolean
    positiveButtonIcon?: string
    cancelable?: boolean
    spinnerResult?: boolean | any
}

@Component({
    name: 'ui-alert-modal',
    components: {
        'ui-modal': Modal,
        'ui-modal-buttons': ModalButtons,
        'ui-button': Button
    },
    emits: ['dismiss', 'resolve']
})
export default class AlertModal extends Vue {

    @Prop({ type: String })
    title: string;

    @Prop({ type: String })
    subtitle: string;

    @Prop({ type: [String, Boolean] })
    cancelButton: string | boolean;

    @Prop({ type: String })
    cancelButtonIcon: string;

    @Prop({ type: [String, Boolean] })
    negativeButton: string | boolean;

    @Prop({ type: String })
    negativeButtonIcon: string;

    @Prop({ type: [String, Boolean] })
    positiveButton: string | boolean;

    @Prop({ type: String })
    positiveButtonIcon: string;

    @Prop({ type: Boolean, default: true })
    cancelable: boolean;

    @Prop({ type: [Boolean, Array], default: undefined })
    spinnerResult: boolean | any[];

    loadingButtonIndex = 0;

    cancel() {

        if(this.loadingButtonIndex === 0 && this.cancelable){
            this.$emit('dismiss');
        }
    }

    get hasButtons() {

        return this.cancelButton || this.negativeButton || this.positiveButton;
    }

    get resolvedSpinnerResult() {

        if(this.spinnerResult === undefined){
            return undefined;
        }

        return isArray(this.spinnerResult) ? this.spinnerResult : [this.spinnerResult];
    }

    close(result, buttonIndex) {

        const spinnerResult = this.resolvedSpinnerResult;

        if(spinnerResult !== undefined && spinnerResult.indexOf(result) > -1){

            this.loadingButtonIndex = buttonIndex;
            this.$emit('resolve', result);
        }
        else {

            this.$emit('dismiss', result);
        }
    }
}

export type AlertModalComp = InstanceType<typeof AlertModal>
export type AlertModalProps = ComponentProps<AlertModalComp>
export const alertModalCompMixin = createComponentMixin(AlertModal)

</script>
<style src="../../../scss/component/modal/AlertModal.scss" lang="scss"></style>